import React, { useState } from 'react';

const termsSections = [
  {
    id: 'acceptance',
    number: '01',
    title: 'Acceptance of Terms',
    content: (
      <>
        <p className="terms-clause-text">
          By accessing the website, engaging in contracts, or utilizing any services provided by <strong>Experts Communication</strong> ("Company", "we", "us", or "our"), you ("Client", "User", "you") agree to be bound by these Terms and Conditions in their entirety.
        </p>
        <p className="terms-clause-text">
          If you do not agree with any part of these terms, you must immediately discontinue use of our website and services. These terms apply to all visitors, clients, partners, and other individuals who access or use our digital marketing, web development, BPO, SEO, and IT services.
        </p>
        <div className="terms-callout-box">
          <strong>Important Note:</strong> Continued engagement following updates to these Terms implies full acceptance of modified provisions.
        </div>
      </>
    )
  },
  {
    id: 'services',
    number: '02',
    title: 'Service Scope & Project Deliverables',
    content: (
      <>
        <p className="terms-clause-text">
          Experts Communication provides digital transformation solutions including, but not limited to, Social Media Marketing, Search Engine Optimization (SEO), Web & eCommerce Development, BPO Services, Logo & Brand Design, and Managed IT Services.
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Scope of Work (SOW):</strong> Each project is defined by a distinct Statement of Work or Service Agreement outlining milestones, timelines, and deliverables.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Change Requests:</strong> Any additions, structural alterations, or out-of-scope revisions will be quoted separately and may alter delivery timelines.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Third-Party Dependencies:</strong> We are not liable for service interruptions caused by third-party APIs, hosting providers, or external algorithms (e.g. Google or Meta algorithm updates).</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'intellectual-property',
    number: '03',
    title: 'Intellectual Property Rights',
    content: (
      <>
        <p className="terms-clause-text">
          We protect your proprietary assets with rigorous legal and ethical safeguards:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Client Ownership:</strong> Upon receipt of full and final payment, all custom deliverables (custom source code, brand graphics, designed assets) become the sole property of the Client.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Company Assets:</strong> Pre-existing frameworks, proprietary development tooling, internal workflows, and generic code libraries remain the intellectual property of Experts Communication.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Portfolio Showcase:</strong> Unless explicitly restricted by an active Non-Disclosure Agreement (NDA), we reserve the right to display completed project case studies in our portfolio.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'client-obligations',
    number: '04',
    title: 'Client Obligations & Content Authenticity',
    content: (
      <>
        <p className="terms-clause-text">
          To ensure timely execution and project success, the Client agrees to:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Provide timely feedback, required credentials, copy, branding materials, and asset approvals within mutually agreed schedules.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Ensure that all text, imagery, trademarks, and multimedia supplied to us do not violate any copyright, trademark, or privacy rights of any third party.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Maintain active communication channels throughout active campaign sprints and milestone reviews.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'payments',
    number: '05',
    title: 'Payment Terms & Milestone Invoicing',
    content: (
      <>
        <p className="terms-clause-text">
          Financial transparency is central to our client partnerships. All project financial terms follow these guidelines:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Retainers & Milestones:</strong> Standard web development and design projects require an initial deposit prior to kickoff, with subsequent milestone disbursements upon approved stage deliveries.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Recurring Subscriptions:</strong> Monthly retainers for Social Media Marketing, SEO, and BPO are billed in advance at the start of each billing cycle.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Late Invoices:</strong> Overdue invoices exceeding 14 calendar days may lead to temporary suspension of active media management or deployment schedules until settled.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'confidentiality',
    number: '06',
    title: 'Confidentiality & Data Protection',
    content: (
      <>
        <p className="terms-clause-text">
          Experts Communication adheres to stringent data protection standards. Both parties agree to safeguard proprietary commercial information, customer databases, strategic plans, and technical architectures disclosed during the engagement.
        </p>
        <div className="terms-callout-box">
          <strong>Non-Disclosure Guarantee:</strong> We enforce encrypted storage, least-privilege credential access, and strict internal NDAs with all engineers, media managers, and consultants.
        </div>
      </>
    )
  },
  {
    id: 'liability',
    number: '07',
    title: 'Warranties & Limitation of Liability',
    content: (
      <>
        <p className="terms-clause-text">
          While we apply industry-leading strategies to drive superior online growth and technical reliability:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>We do not guarantee specific monetary returns or fixed organic rank positions due to fluctuating market factors and external platform algorithms.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>In no event shall Experts Communication be liable for indirect, punitive, or consequential damages arising from business interruptions or lost revenue. Total aggregate liability is limited to the fees paid for the specific service under dispute.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'termination',
    number: '08',
    title: 'Termination & Cancellation Policy',
    content: (
      <>
        <p className="terms-clause-text">
          Either party may terminate a service agreement under the following terms:
        </p>
        <ul className="terms-clause-bullets">
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Monthly Retainers:</strong> A written notice of 30 days is required for recurring marketing or BPO contracts.</span>
          </li>
          <li className="terms-clause-bullet">
            <svg className="terms-bullet-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span><strong>Fixed Milestone Projects:</strong> The Client is responsible for paying for all work completed up to the official termination date. Work products will be handed over upon settlement.</span>
          </li>
        </ul>
      </>
    )
  },
  {
    id: 'governing-law',
    number: '09',
    title: 'Governing Law & Dispute Resolution',
    content: (
      <>
        <p className="terms-clause-text">
          These Terms and Conditions shall be governed by and construed in accordance with the laws of Pakistan. Any dispute or claim arising out of or in connection with our services shall first be resolved through good-faith mutual negotiation, failing which it shall be submitted to the exclusive jurisdiction of the competent courts of Karachi, Pakistan.
        </p>
      </>
    )
  }
];

function TermsPage() {
  const [activeNav, setActiveNav] = useState('acceptance');

  React.useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 160;
      for (const section of termsSections) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveNav(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToClause = (id) => {
    setActiveNav(id);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -110;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleContactClick = (e) => {
    e.preventDefault();
    window.location.href = '/contact/';
  };

  return (
    <div className="terms-page-wrapper">
      {/* Hero Header */}
      <section className="terms-hero-section">
        <div className="container">
          <h1 className="terms-hero-title">
            Terms &amp; <span style={{ color: '#c40a0a' }}>Conditions</span>
          </h1>

          <p className="terms-hero-subtitle">
            Welcome to Experts Communication. Please review the terms, obligations, and service standards governing our partnerships, digital deliverables, and consulting solutions.
          </p>

          {/* Quick Highlight Cards */}
          <div className="terms-highlights-grid">
            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>100% IP Protection</h4>
                <p>Full ownership upon final delivery</p>
              </div>
            </div>

            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                  <polyline points="10 9 9 9 8 9" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>Transparent SOW</h4>
                <p>Defined milestone deliverables</p>
              </div>
            </div>

            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <polyline points="9 12 11 14 15 10" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>Strict NDA Terms</h4>
                <p>Enterprise data confidentiality</p>
              </div>
            </div>

            <div className="terms-highlight-card">
              <div className="terms-highlight-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 14 14" />
                </svg>
              </div>
              <div className="terms-highlight-text">
                <h4>Last Updated</h4>
                <p>September 2026 Edition</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Body Section with Sticky TOC Sidebar */}
      <section className="terms-body-section">
        <div className="container">
          <div className="terms-layout-grid">
            {/* Left Sticky Table of Contents */}
            <aside className="terms-toc-sidebar">
              <h3 className="terms-toc-title">Table of Contents</h3>
              <nav className="terms-toc-nav">
                {termsSections.map((item) => (
                  <button
                    key={item.id}
                    className={`terms-toc-link ${activeNav === item.id ? 'active' : ''}`}
                    onClick={() => scrollToClause(item.id)}
                  >
                    <span style={{ color: '#c40a0a', fontWeight: '800' }}>{item.number}.</span>
                    <span>{item.title}</span>
                  </button>
                ))}
              </nav>
            </aside>

            {/* Right Clause Details */}
            <main className="terms-content-area">
              {termsSections.map((section) => (
                <div key={section.id} id={section.id} className="terms-clause-card">
                  <div className="terms-clause-header">
                    <div className="terms-clause-number">{section.number}</div>
                    <h2 className="terms-clause-title">{section.title}</h2>
                  </div>
                  {section.content}
                </div>
              ))}

              {/* Legal Support & Contact Advisory Box */}
              <div className="terms-support-card">
                <h3 className="terms-support-title">Have Questions Regarding Our Terms?</h3>
                <p className="terms-support-desc">
                  Our compliance team and client advisors are here to provide full clarity on project statements of work, NDAs, and custom enterprise service level agreements.
                </p>
                <div className="terms-support-actions">
                  <button className="btn-terms-contact" onClick={handleContactClick}>
                    <span>Contact Advisory Team</span>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </button>
                  <a href="mailto:info@expertscommunication.net" className="btn-terms-email">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                    <span>info@expertscommunication.net</span>
                  </a>
                </div>
              </div>
            </main>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TermsPage;
